import { useMemo, useRef, useState } from 'react'

import { CalendarAppointmentBlock } from '@/components/calendar/CalendarAppointmentBlock'
import { layoutAppointmentLanes } from '@/lib/calendar/appointmentLayout'
import {
  PX_PER_MINUTE,
  buildReschedulePayload,
  buildTimeSlotLabels,
  dropOffsetToMinutes,
  parseTimeToMinutes,
} from '@/lib/calendar/dayCalendar'
import {
  appointmentsForDay,
  formatDayColumnLabel,
  hoursForDay,
  isToday,
} from '@/lib/calendar/weekCalendar'
import { cn } from '@/lib/utils'

/**
 * @param {import('@/types/api').Stylist[]} stylists
 */
function stylistColourMap(stylists) {
  return new Map(stylists.map((stylist) => [stylist.id, stylist.colour]))
}

/**
 * @param {{
 *   weekDates: string[]
 *   appointments: import('@/types/api').Appointment[]
 *   stylists: import('@/types/api').Stylist[]
 *   businessHours: Record<string, { open: string, close: string }>
 *   openTime: string
 *   closeTime: string
 *   slotMinutes: number
 *   today: string
 *   onReschedule: (appointmentId: number, payload: { starts_at: string, stylist_id?: number | null }) => Promise<void>
 *   onSelectAppointment?: (appointment: import('@/types/api').Appointment) => void
 *   rescheduleEnabled?: boolean
 *   onSelectDay?: (dayDate: string) => void
 *   reschedulingId?: number | null
 * }} props
 */
export function WeekCalendar({
  weekDates,
  appointments,
  stylists,
  businessHours,
  openTime,
  closeTime,
  slotMinutes,
  today,
  onReschedule,
  onSelectAppointment,
  rescheduleEnabled = false,
  onSelectDay,
  reschedulingId = null,
}) {
  const [draggingId, setDraggingId] = useState(null)
  const columnRefs = useRef({})
  const colours = useMemo(() => stylistColourMap(stylists), [stylists])

  const dayStartMinutes = parseTimeToMinutes(openTime)
  const dayEndMinutes = parseTimeToMinutes(closeTime)
  const gridHeight = (dayEndMinutes - dayStartMinutes) * PX_PER_MINUTE
  const slots = buildTimeSlotLabels(dayStartMinutes, dayEndMinutes, slotMinutes)

  const appointmentsByDay = useMemo(
    () =>
      Object.fromEntries(
        weekDates.map((date) => [date, appointmentsForDay(appointments, date)]),
      ),
    [appointments, weekDates],
  )

  const laneLayouts = useMemo(() => {
    /** @type {Record<string, Map<number, { column: number, totalColumns: number }>>} */
    const layouts = {}

    for (const dayDate of weekDates) {
      layouts[dayDate] = layoutAppointmentLanes(appointmentsByDay[dayDate] ?? [])
    }

    return layouts
  }, [appointmentsByDay, weekDates])

  /**
   * @param {import('@/types/api').Appointment} appointment
   * @param {string} dayDate
   * @param {DragEvent} event
   */
  async function handleDrop(appointment, dayDate, event) {
    event.preventDefault()

    const columnElement = columnRefs.current[dayDate]
    if (!columnElement) {
      return
    }

    const dayHours = hoursForDay(businessHours, dayDate)
    const columnOpen = parseTimeToMinutes(dayHours.open)
    const columnClose = parseTimeToMinutes(dayHours.close)

    const offsetY = event.clientY - columnElement.getBoundingClientRect().top + columnElement.scrollTop
    const dropMinutes = dropOffsetToMinutes(offsetY, dayStartMinutes, PX_PER_MINUTE, slotMinutes)
    const maxStart = columnClose - (appointment.duration_minutes ?? slotMinutes)

    if (dropMinutes < columnOpen || dropMinutes > maxStart) {
      setDraggingId(null)
      return
    }

    const payload = buildReschedulePayload(
      appointment,
      appointment.stylist_id,
      dayDate,
      dropMinutes,
    )

    try {
      await onReschedule(appointment.id, payload)
    } finally {
      setDraggingId(null)
    }
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <div
          className="grid min-w-max"
          style={{ gridTemplateColumns: `72px repeat(${weekDates.length}, minmax(140px, 1fr))` }}
        >
          <div className="sticky left-0 z-20 border-b border-r bg-muted/30 px-2 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Time
          </div>

          {weekDates.map((dayDate) => {
            const label = formatDayColumnLabel(dayDate)
            const count = appointmentsByDay[dayDate]?.length ?? 0

            return (
              <button
                key={`${dayDate}-header`}
                type="button"
                onClick={() => onSelectDay?.(dayDate)}
                className={cn(
                  'border-b px-3 py-3 text-left transition-colors hover:bg-muted/30',
                  isToday(dayDate, today) && 'bg-primary/[0.06]',
                )}
              >
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {label.weekday}
                </p>
                <p
                  className={cn(
                    'text-lg font-bold tabular-nums',
                    isToday(dayDate, today) ? 'text-primary' : 'text-foreground',
                  )}
                >
                  {label.day}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {count} booking{count === 1 ? '' : 's'}
                </p>
              </button>
            )
          })}

          <div className="sticky left-0 z-20 border-r bg-muted/20">
            <div className="relative" style={{ height: gridHeight }}>
              {slots.map((slot) => (
                <div
                  key={slot.minutes}
                  className="absolute left-0 right-0 border-t border-border/50 px-2 text-[10px] tabular-nums text-muted-foreground"
                  style={{ top: (slot.minutes - dayStartMinutes) * PX_PER_MINUTE }}
                >
                  <span className="relative -top-2 bg-muted/20 pr-1">{slot.label}</span>
                </div>
              ))}
            </div>
          </div>

          {weekDates.map((dayDate) => (
            <div
              key={`${dayDate}-body`}
              ref={(element) => {
                columnRefs.current[dayDate] = element
              }}
              className={cn(
                'relative border-r border-border/60',
                isToday(dayDate, today) && 'bg-primary/[0.03]',
                draggingId && rescheduleEnabled && 'bg-primary/[0.02]',
              )}
              style={{ height: gridHeight }}
              onDragOver={(event) => {
                if (!rescheduleEnabled) {
                  return
                }

                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
              }}
              onDrop={(event) => {
                if (!rescheduleEnabled) {
                  return
                }

                const appointment = appointments.find((item) => item.id === draggingId)
                if (!appointment) {
                  return
                }

                handleDrop(appointment, dayDate, event)
              }}
            >
              {slots.map((slot) => (
                <div
                  key={`${dayDate}-${slot.minutes}`}
                  className="pointer-events-none absolute left-0 right-0 border-t border-border/30"
                  style={{ top: (slot.minutes - dayStartMinutes) * PX_PER_MINUTE }}
                />
              ))}

              {(appointmentsByDay[dayDate] ?? []).map((appointment) => (
                <CalendarAppointmentBlock
                  key={appointment.id}
                  appointment={appointment}
                  stylistColour={colours.get(appointment.stylist_id) ?? null}
                  dayStartMinutes={dayStartMinutes}
                  pxPerMinute={PX_PER_MINUTE}
                  lane={laneLayouts[dayDate]?.get(appointment.id)}
                  compact
                  rescheduling={reschedulingId === appointment.id}
                  rescheduleEnabled={rescheduleEnabled}
                  onDragStart={(item) => setDraggingId(item.id)}
                  onSelect={onSelectAppointment}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {stylists.length > 0 && (
        <div className="flex flex-wrap gap-3 border-t px-4 py-3">
          {stylists
            .filter((stylist) => stylist.is_active)
            .map((stylist) => (
              <span key={stylist.id} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: stylist.colour ?? 'hsl(var(--primary))' }}
                />
                {stylist.name}
              </span>
            ))}
        </div>
      )}
    </div>
  )
}
